import { jsxs, jsx, Fragment as Fragment$1 } from 'react/jsx-runtime';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { mediaMobile, mediaHover } from '@fabric-app/ui/constants/styled-utils';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { styled, css } from 'styled-components';
import { ToolbarItemType } from '../toolbar.types.js';
import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import Tooltip from '@fabric-app/ui/components/Tooltip';
import { IconChevronDown } from '@fabric-app/icons/svg';
import { forwardRef, useRef, useMemo, useEffect, Fragment, useState, useCallback } from 'react';
import { useDomContentRect } from '@fabric-app/hooks/useDomContentRect';
import { isFunction } from '@fabric-app/utils/guards';
import { mergeRefs } from 'react-merge-refs';
import { EditorSelector } from '../../Tiptap/Components/EditorSelector.js';

const ToolbarRoot = styled.div.attrs((p) => {
  return {
    role: "toolbar",
    "aria-orientation": "horizontal",
    ...p
  };
}).withConfig({
  displayName: "Toolbar__ToolbarRoot",
  componentId: "sc-bdf0458b-0"
})([
  `display:flex;width:100%;min-width:100%;max-width:100%;flex-wrap:nowrap;cursor:default;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;z-index:150;display:flex;align-items:center;background:var(--fabric-color-bg-primary);backdrop-filter:blur(8px);pointer-events:auto;border-top:1px solid var(--fabric-color-border-primary);border-bottom:1px solid var(--fabric-color-border-primary);&&{user-select:none;}&[data-floating]{border:1px solid var(--fabric-color-border-primary);box-shadow:0 2px 8px 1px rgba(128,128,128,0.187);background:var(--fabric-color-bg-primary);backdrop-filter:blur(8px);border-radius:12px;flex-wrap:nowrap;width:fit-content;max-width:none;}`
]);
const ToolbarContent = styled.div.withConfig({
  displayName: "Toolbar__ToolbarContent",
  componentId: "sc-bdf0458b-1"
})([
  `display:flex;flex-grow:1;overflow-x:visible;align-items:center;padding:4px;`,
  `{padding:0.45rem 0.5rem;}flex-wrap:nowrap;width:100%;min-width:100%;max-width:100%;flex-shrink:0;`
], mediaMobile);
const ToolbarScrollbar = styled(ScrollArea.Default).attrs((p) => {
  return {
    orientation: "horizontal",
    scrollbarVariant: "none",
    asFlex: true,
    innerDisplayFlex: true,
    containOverscroll: true,
    ...p
  };
}).withConfig({
  displayName: "Toolbar__ToolbarScrollbar",
  componentId: "sc-bdf0458b-2"
})([
  `flex-grow:1;`
]);
const ToolbarGroup = styled.div.attrs((p) => {
  return {
    role: "group",
    ...p
  };
}).withConfig({
  displayName: "Toolbar__ToolbarGroup",
  componentId: "sc-bdf0458b-3"
})([
  `display:flex;align-items:center;flex-wrap:nowrap;gap:4px;&:empty{display:none;}[data-floating] &{display:flex;flex-wrap:nowrap;align-items:center;gap:6px;}& + &{margin-left:6px;}`
]);
const ToolbarSeparator = styled.div.withConfig({
  displayName: "Toolbar__ToolbarSeparator",
  componentId: "sc-bdf0458b-4"
})([
  `width:1px;flex-shrink:0;background-color:`,
  `;margin:0 6px;`
], cssVar["color-bg-primary"]);
const ButtonStyles = css([
  `position:relative;border-radius:10px;border:none;padding:0.313rem;display:flex;align-items:center;justify-content:center;height:1.875rem;min-width:1.875rem;width:min-content;font-size:0.813rem;margin:0;outline:none;font-weight:500;color:var(--fabric-color-text-tertiary);background:none;`,
  `{&:hover{color:var(--fabric-color-text-primary);background:var(--fabric-color-bg-tertiary);}}&:focus{position:relative;color:var(--fabric-color-text-primary);background:var(--fabric-color-bg-tertiary);}&:focus-visible{outline:none;}`,
  `{height:2.25rem;min-width:2.25rem;padding:0.5rem;}&:disabled{opacity:0.5;cursor:not-allowed;}&[data-variant='secondary']{color:`,
  `;background:`,
  `;`,
  `{&:hover{color:`,
  `;background:`,
  `;}}}`
], mediaHover, mediaMobile, cssVar["color-text-primary"], cssVar["color-bg-secondary"], mediaHover, cssVar["color-text-primary"], cssVar["color-bg-tertiary"]);
const ToolbarButtonLabel = styled.span.withConfig({
  displayName: "Toolbar__ToolbarButtonLabel",
  componentId: "sc-bdf0458b-5"
})([
  ``
]);
const ToolbarToggleItem = styled.button.withConfig({
  displayName: "Toolbar__ToolbarToggleItem",
  componentId: "sc-bdf0458b-6"
})([
  ``,
  ` margin-left:2px;`,
  `{display:none;}&:first-child{margin-left:0;}&[data-state='on']{color:`,
  `;background:`,
  `;}`
], ButtonStyles, ToolbarButtonLabel, cssVar["color-text-primary"], cssVar["color-bg-quarternary"]);
const ToggleSelectorButton = styled(ToolbarToggleItem).withConfig({
  displayName: "Toolbar__ToggleSelectorButton",
  componentId: "sc-bdf0458b-7"
})([
  `display:flex;align-items:center;white-space:nowrap;border:1px solid `,
  `;border-radius:8px;gap:4px;`
], cssVar["color-border-primary"]);
const ToolbarButtonEl = styled.button.withConfig({
  displayName: "Toolbar__ToolbarButtonEl",
  componentId: "sc-bdf0458b-8"
})([
  ``,
  ` `,
  `{display:none;}`
], ButtonStyles, ToolbarButtonLabel);
const TriggerContent = styled.span.withConfig({
  displayName: "Toolbar__TriggerContent",
  componentId: "sc-bdf0458b-9"
})([
  `display:flex;align-items:center;flex-grow:1;gap:12px;`
]);
const ToolbarButtonShortcut = styled.span.withConfig({
  displayName: "Toolbar__ToolbarButtonShortcut",
  componentId: "sc-bdf0458b-10"
})([
  `display:flex;justify-content:flex-end;align-items:center;flex-shrink:0;`,
  `{display:none;}`
], mediaMobile);
const ToolbarToggleComponent = ({ item: { label, icon, onClick, getIsActive, shortcut, isHidden }, insideDropdown = false }) => {
  const isActive = getIsActive();
  const labelText = typeof label === "function" ? label() : label;
  const renderContent = () => {
    const content = /* @__PURE__ */ jsx(ToolbarToggleItem, {
      "data-state": isActive ? "on" : "off",
      onClick: () => onClick(),
      "aria-label": labelText,
      "aria-haspopup": insideDropdown,
      "aria-expanded": insideDropdown && isActive,
      type: "button",
      children: insideDropdown ? /* @__PURE__ */ jsxs(Fragment$1, {
        children: [
          /* @__PURE__ */ jsxs(TriggerContent, {
            children: [
              icon,
              /* @__PURE__ */ jsx(ToolbarButtonLabel, {
                children: labelText
              })
            ]
          }),
          shortcut && /* @__PURE__ */ jsx(ToolbarButtonShortcut, {
            children: shortcut
          })
        ]
      }) : icon
    });
    if (insideDropdown) {
      return /* @__PURE__ */ jsx(RadixDropdown.DropdownMenuItem, {
        asChild: true,
        children: content
      });
    }
    return /* @__PURE__ */ jsx(Tooltip, {
      label: /* @__PURE__ */ jsxs(Fragment$1, {
        children: [
          label,
          shortcut
        ]
      }),
      children: content
    });
  };
  if (isHidden?.()) return null;
  return renderContent();
};
const ToolbarButtonComponent = ({ item: { key, label, icon, mobileIcon, onClick, shortcut, isDisabled: isDisabledFn, variant = "default", isHidden }, insideDropdown = false }) => {
  const isDisabled = isDisabledFn?.();
  const labelText = typeof label === "function" ? label() : label;
  const renderContent = () => {
    if (insideDropdown) {
      return /* @__PURE__ */ jsx(EditorSelector.Item, {
        onClick: () => onClick(),
        "aria-label": labelText,
        disabled: isDisabled,
        value: key,
        label: labelText,
        icon,
        mobileIcon
      });
    }
    return /* @__PURE__ */ jsx(Tooltip, {
      label: /* @__PURE__ */ jsxs(Fragment$1, {
        children: [
          labelText,
          shortcut
        ]
      }),
      children: /* @__PURE__ */ jsx(ToolbarButtonEl, {
        "data-inside-dropdown": insideDropdown,
        onClick: () => onClick(),
        "aria-label": labelText,
        "aria-haspopup": insideDropdown,
        type: "button",
        disabled: Boolean(isDisabled),
        "data-variant": variant,
        children: insideDropdown ? /* @__PURE__ */ jsxs(Fragment$1, {
          children: [
            /* @__PURE__ */ jsxs(TriggerContent, {
              children: [
                icon,
                /* @__PURE__ */ jsx(ToolbarButtonLabel, {
                  children: labelText
                })
              ]
            }),
            shortcut && /* @__PURE__ */ jsx(ToolbarButtonShortcut, {
              children: shortcut
            })
          ]
        }) : icon ? icon : labelText
      })
    });
  };
  if (isHidden?.()) return null;
  return renderContent();
};
const Chevron = styled(IconChevronDown).withConfig({
  displayName: "Toolbar__Chevron",
  componentId: "sc-bdf0458b-11"
})([
  `[data-state='on'] &{transform:rotate(180deg);}`
]);
const ToolbarSelectorComponent = ({ item, mobileTarget }) => {
  const [isOpen, setIsOpen] = useState(false);
  const labelText = typeof item.label === "function" ? item.label() : item.label;
  const value = useMemo(() => {
    return typeof item.value === "function" ? item.value() : item.value;
  }, [
    item
  ]);
  const valueRender = useMemo(() => {
    const option = item.options.find((option2) => option2.value === value);
    if (!option) {
      console.warn(`Option with value ${value} not found in options`, item);
      return null;
    }
    return option.label;
  }, [
    item,
    value
  ]);
  const onSelect = useCallback((value2) => {
    const option = item.options.find((option2) => option2.value === value2);
    if (!option) {
      console.warn(`Option with value ${value2} not found in options`, item);
      return;
    }
    if (option.onSelect) option.onSelect();
    else item.onSelect?.(value2);
    setIsOpen(false);
  }, [
    item
  ]);
  if (item.isHidden?.(item)) return null;
  return /* @__PURE__ */ jsx(EditorSelector.Base, {
    label: labelText,
    open: isOpen,
    onOpenChange: setIsOpen,
    mobileTarget,
    renderButton: ({ open, onOpenChange, props }) => /* @__PURE__ */ jsxs(ToggleSelectorButton, {
      "data-state": open ? "on" : "off",
      onClick: (e) => {
        e.preventDefault();
        onOpenChange(!open);
      },
      ...props,
      children: [
        valueRender,
        /* @__PURE__ */ jsx(Chevron, {})
      ]
    }),
    children: /* @__PURE__ */ jsxs(EditorSelector.Group, {
      children: [
        labelText && /* @__PURE__ */ jsxs(EditorSelector.GroupTitle, {
          children: [
            labelText,
            ":"
          ]
        }),
        item.options.map((option) => {
          return /* @__PURE__ */ jsx(EditorSelector.Item, {
            value: option.value,
            onClick: () => onSelect(option.value),
            label: option.label,
            icon: option.icon,
            mobileIcon: option.mobileIcon
          }, option.value);
        })
      ]
    })
  });
};
const ToolbarDropdownComponent = ({ item: { icon, label, items } }) => {
  const labelText = typeof label === "function" ? label() : label;
  return /* @__PURE__ */ jsx(EditorSelector.Base, {
    label: labelText,
    renderButton: ({ open, onOpenChange, props }) => /* @__PURE__ */ jsxs(ToggleSelectorButton, {
      "data-state": open ? "on" : "off",
      onClick: (e) => {
        e.preventDefault();
        onOpenChange(!open);
      },
      ...props,
      children: [
        icon,
        " ",
        labelText,
        /* @__PURE__ */ jsx(Chevron, {})
      ]
    }),
    children: renderDropdownContent(items)
  });
};
const renderDropdownContent = (items) => {
  return items.map((item) => {
    const label = isFunction(item.label) ? item.label() : item.label;
    return /* @__PURE__ */ jsxs(EditorSelector.Group, {
      children: [
        label && /* @__PURE__ */ jsx(EditorSelector.GroupTitle, {
          children: label
        }),
        renderDropdownGroupContent(item.items)
      ]
    }, `dropdown-group-${item.name}`);
  });
};
const renderDropdownGroupContent = (items) => {
  return items.map((item) => {
    switch (item.type) {
      case ToolbarItemType.Toggle:
        return /* @__PURE__ */ jsx(ToolbarToggleComponent, {
          item,
          insideDropdown: true
        }, `dropdown-item-${item.key}`);
      case ToolbarItemType.Button:
        return /* @__PURE__ */ jsx(ToolbarButtonComponent, {
          item,
          insideDropdown: true
        }, `dropdown-item-${item.key}`);
      case ToolbarItemType.Custom:
        return item.render(`dropdown-item-${item.key}`, true);
    }
  });
};
const renderToolbarGroupItems = (items, mobileTarget) => {
  return items.map((item) => {
    switch (item.type) {
      case ToolbarItemType.Toggle:
        return /* @__PURE__ */ jsx(ToolbarToggleComponent, {
          item
        }, item.key);
      case ToolbarItemType.Dropdown:
        return /* @__PURE__ */ jsx(ToolbarDropdownComponent, {
          item
        }, item.key);
      case ToolbarItemType.Button:
        return /* @__PURE__ */ jsx(ToolbarButtonComponent, {
          item
        }, item.key);
      case ToolbarItemType.Selector:
        return /* @__PURE__ */ jsx(ToolbarSelectorComponent, {
          item,
          mobileTarget
        }, item.key);
      case ToolbarItemType.Custom:
        return item.render(item.key, false);
    }
  });
};
const ToolbarBase = /* @__PURE__ */ forwardRef(({ children, groups, floating, onToolbarHeightChange, noSeparator, mobileTarget, suffix, ...props }, ref) => {
  const toolbarRef = useRef(null);
  const [toolbarRect] = useDomContentRect(toolbarRef.current);
  const mergedRefs = useMemo(() => mergeRefs([
    ref,
    toolbarRef
  ]), [
    ref,
    toolbarRef
  ]);
  useEffect(() => {
    onToolbarHeightChange?.(toolbarRect?.height || 0);
  }, [
    toolbarRect?.height,
    onToolbarHeightChange
  ]);
  const mappedGroups = useMemo(() => groups.filter(({ isHidden }) => !isHidden?.()).map(({ name, label, items }, index, arr) => {
    return /* @__PURE__ */ jsxs(Fragment, {
      children: [
        /* @__PURE__ */ jsx(ToolbarGroup, {
          "aria-label": typeof label === "function" ? label() : label,
          children: renderToolbarGroupItems(items, mobileTarget)
        }, name),
        index < arr.length - 1 && !noSeparator && /* @__PURE__ */ jsx(ToolbarSeparator, {
          role: "separator"
        }, `separator-${name}`)
      ]
    }, `fragment-${name}`);
  }), [
    groups,
    mobileTarget,
    noSeparator
  ]);
  const hash = useMemo(() => {
    return mappedGroups.map((group) => group.key).join("-");
  }, [
    mappedGroups
  ]);
  if (mappedGroups.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxs(ToolbarRoot, {
    ref: mergedRefs,
    draggable: false,
    "data-floating": floating,
    ...props,
    children: [
      /* @__PURE__ */ jsx(ToolbarScrollbar, {
        children: /* @__PURE__ */ jsx(ToolbarContent, {
          children: mappedGroups
        })
      }),
      suffix
    ]
  }, hash);
});
ToolbarBase.displayName = "ToolbarBase";
const Toolbar = {
  Root: ToolbarRoot,
  Scrollbar: ToolbarScrollbar,
  Content: ToolbarContent,
  Group: ToolbarGroup,
  Separator: ToolbarSeparator,
  Toggle: ToolbarToggleComponent,
  Button: ToolbarButtonComponent,
  Selector: ToolbarSelectorComponent,
  Dropdown: ToolbarDropdownComponent,
  Base: ToolbarBase
};

export { Toolbar, ToolbarButtonComponent, ToolbarContent, ToolbarGroup, ToolbarRoot, ToolbarScrollbar, ToolbarSeparator, ToolbarToggleComponent, ToolbarToggleItem };
//# sourceMappingURL=Toolbar.js.map
